/**
 * Crypto wallet utilities — validation, masking, constants.
 * Subcollection: users/{userId}/crypto_wallets/{walletId}
 *
 * wallet_type values:
 *   "withdrawal" — client receives funds here (current use case: saque)
 *   "deposit"    — client sends funds from here; company has one receiving wallet (future)
 */

export const CRYPTO_ASSETS = ["USDT", "USDC", "BTC"];
export const CRYPTO_NETWORKS = ["POLYGON", "BSC", "ETHEREUM", "TRON", "BITCOIN"];

/** Exchange or app that holds the deposit address. Empty means not identified. */
export const WALLET_PROVIDERS = [
  "Revolut",
  "Crypto.com",
  "Binance",
  "Bybit",
  "OKX",
  "Kraken",
  "Redot",
];

/** Brand colors. Solid fill so dark brand blues stay readable on the dark admin. */
export const PROVIDER_COLORS = {
  Revolut: { bg: "#1326FD", text: "#FFFFFF", border: "#1326FD" },
  "Crypto.com": { bg: "#002D74", text: "#FFFFFF", border: "#1199FA" },
  Binance: { bg: "#FCD535", text: "#1E2329", border: "#F0B90B" },
  Bybit: { bg: "#F7A600", text: "#121214", border: "#F7A600" },
  OKX: { bg: "#FFFFFF", text: "#000000", border: "#FFFFFF" },
  Kraken: { bg: "#5741D9", text: "#FFFFFF", border: "#5741D9" },
  Redot: { bg: "#E41B38", text: "#FFFFFF", border: "#E41B38" },
};

export const inferWalletProviderFromLabel = (label) => {
  const text = String(label || "").toLowerCase();
  if (text.includes("revolut")) return "Revolut";
  if (text.includes("crypto.com") || text.includes("crypto com")) return "Crypto.com";
  if (text.includes("binance")) return "Binance";
  if (text.includes("bybit")) return "Bybit";
  if (text.includes("okx")) return "OKX";
  if (text.includes("kraken")) return "Kraken";
  if (text.includes("redot")) return "Redot";
  return "";
};

export const DEFAULT_ASSET = "USDT";
export const DEFAULT_NETWORK = "POLYGON";
export const MAX_ACTIVE_WALLETS_PER_USER = 3;

export const WALLET_TYPES = {
  WITHDRAWAL: "withdrawal",
  DEPOSIT: "deposit",
};
export const DEFAULT_WALLET_TYPE = WALLET_TYPES.WITHDRAWAL;

/** Cooldown before auto-pay can use a new or recently edited wallet (24h). */
export const WALLET_COOLDOWN_MS = 24 * 60 * 60 * 1000;
export const MAX_EDIT_HISTORY_ENTRIES = 20;

/**
 * Security fields on users/{userId}/crypto_wallets/{walletId}:
 * - auto_pay_eligible_from: Timestamp — set on create (now + 24h)
 * - edit_suspended_until: Timestamp — set on admin edit (now + 24h)
 * - last_edited_at / last_edited_by: last metadata edit
 * - edit_history: array of { edited_at, edited_by, changes: { field: { from, to } } }
 */
export const getWalletCooldownUntil = () => new Date(Date.now() + WALLET_COOLDOWN_MS);

export const buildWalletEditChanges = (before, after) => {
  const changes = {};
  const prevLabel = (before?.label || "").trim();
  const nextLabel = (after?.label || "").trim();
  if (nextLabel !== prevLabel) {
    changes.label = { from: prevLabel || "—", to: nextLabel || "—" };
  }
  if (after?.asset !== before?.asset) {
    changes.asset = { from: before?.asset || DEFAULT_ASSET, to: after?.asset };
  }
  if (after?.network !== before?.network) {
    changes.network = { from: before?.network || DEFAULT_NETWORK, to: after?.network };
  }
  const prevProvider = before?.provider || "";
  const nextProvider = after?.provider || "";
  if (nextProvider !== prevProvider) {
    changes.provider = { from: prevProvider || "—", to: nextProvider || "—" };
  }
  if (!!after?.is_default !== !!before?.is_default) {
    changes.is_default = { from: !!before?.is_default, to: !!after?.is_default };
  }
  return changes;
};

export const maskAddress = (address, network = "POLYGON") => {
  if (!address || typeof address !== "string") return "--";
  const trimmed = address.trim();
  if (trimmed.length <= 10) return trimmed;
  return `${trimmed.substring(0, 4)}…${trimmed.substring(trimmed.length - 4)}`;
};

export const getAddressLast4 = (address) => {
  if (!address || typeof address !== "string") return "";
  const trimmed = address.trim();
  return trimmed.length >= 4 ? trimmed.slice(-4) : trimmed;
};

export const formatWalletDisplay = (wallet) => {
  if (!wallet) return "--";
  const label = (wallet.label || "").trim();
  const id = wallet.id || wallet.wallet_id || "--";
  const name = label || id;
  const asset = wallet.asset || DEFAULT_ASSET;
  const network = wallet.network || DEFAULT_NETWORK;
  const masked = maskAddress(wallet.address, network);
  return `${name} · ${asset} · ${network} · ${masked}`;
};

/** Admin withdrawal list: label + last 4 address chars (manual payout reference). */
export const formatWithdrawalWalletAdminDisplay = (row) => {
  const label = (row?.selected_wallet_label || "").trim();
  const last4 = getAddressLast4(row?.selected_wallet_address);
  if (label && last4) return `${label} · …${last4}`;
  if (last4) return `…${last4}`;
  if (label) return label;
  if (row?.selected_wallet_display) return row.selected_wallet_display;
  return "--";
};

export const getWithdrawalWalletTooltip = (row) => {
  const lines = [];
  if (row?.selected_wallet_label) lines.push(`Label: ${row.selected_wallet_label}`);
  if (row?.selected_wallet_asset) {
    lines.push(
      `Asset: ${row.selected_wallet_asset}${row.selected_wallet_network ? ` (${row.selected_wallet_network})` : ""}`
    );
  }
  if (row?.selected_wallet_address) lines.push(`Address: ${row.selected_wallet_address}`);
  return lines.length ? lines.join("\n") : formatWithdrawalWalletAdminDisplay(row);
};

export const validateWalletAddress = (address, network) => {
  if (!address || typeof address !== "string") {
    return { valid: false, message: "Address is required" };
  }
  const trimmed = address.trim();

  switch (network) {
    case "POLYGON":
    case "BSC":
    case "ETHEREUM":
      if (!/^0x[a-fA-F0-9]{40}$/.test(trimmed)) {
        return { valid: false, message: "Invalid EVM address (0x + 40 hex characters)" };
      }
      return { valid: true };
    case "TRON":
      if (!/^T[a-zA-Z0-9]{33}$/.test(trimmed)) {
        return { valid: false, message: "Invalid TRON address (T + 33 characters)" };
      }
      return { valid: true };
    case "BITCOIN":
      if (
        !/^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,62}$/.test(trimmed) &&
        !/^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(trimmed)
      ) {
        return { valid: false, message: "Invalid Bitcoin address format" };
      }
      return { valid: true };
    default:
      return { valid: false, message: "Unsupported network" };
  }
};

export const getWithdrawalWalletDisplay = (row) => formatWithdrawalWalletAdminDisplay(row);

/**
 * Active wallet created before security fields (auto_pay_eligible_from, edit_history).
 * Used for admin review — auto-pay skips until backfilled or edited.
 */
export const isLegacySecurityWallet = (wallet) => {
  if (!wallet || wallet.is_active === false) return false;
  if (!wallet.auto_pay_eligible_from) return true;
  if (!Array.isArray(wallet.edit_history)) return true;
  return false;
};

/**
 * Client initials from the full name (e.g. "Alessandra Palmeston" → "AP").
 * Single name → first two letters; empty → "??".
 */
export const getClientInitials = (fullName) => {
  const parts = (fullName || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

/** Bybit (and most exchanges) cap the wallet remark/label at 20 characters. */
export const WALLET_REFERENCE_MAX_LENGTH = 20;

/**
 * Account-number-style reference for a client, safe to paste as a wallet label
 * on Bybit and reusable across the platform.
 *
 * Format: `INITIALS-<user_id prefix>` (no parentheses), capped at `maxLength`.
 * Derived from immutable data (name initials + Firestore user_id), so it is
 * stable per client. The user_id portion is a prefix, so it stays searchable;
 * the full user_id is still shown separately for exact lookup.
 *
 * Examples:
 *   buildWalletReference("Alessandra Palmeston", "5X3rXWN0V1Wf4dQyQoBSHXfiayP2")
 *     → "AP-5X3rXWN0V1Wf4dQyQ"
 */
export const buildWalletReference = (
  fullName,
  userId,
  maxLength = WALLET_REFERENCE_MAX_LENGTH
) => {
  const initials = getClientInitials(fullName);
  const id = (userId || "").trim();
  if (!id) return initials;
  const prefix = `${initials}-`;
  const idChars = Math.max(0, maxLength - prefix.length);
  return `${prefix}${id.slice(0, idChars)}`;
};

export const generateWalletId = (existingWallets = []) => {
  const used = new Set(
    existingWallets.map((w) => w.id || w.wallet_id).filter(Boolean)
  );
  for (let i = 1; i <= MAX_ACTIVE_WALLETS_PER_USER; i += 1) {
    const id = `wallet_${i}`;
    if (!used.has(id)) return id;
  }
  return `wallet_${Date.now()}`;
};
